import { ParsedLeadRow, ValidationError, ValidationWarning } from './csv-parser.helper';
import { parseAddress } from './address-parser.helper';
import { US_STATES } from './state-mapping.helper';

// Parse state input - supports both full name (Alabama) and abbreviation (AL)
function parseStateInput(input: string): string | null {
  const trimmed = input.trim().toUpperCase();

  // Check if it's already a 2-letter code
  if (trimmed.length === 2 && Object.values(US_STATES).includes(trimmed)) {
    return trimmed;
  }

  // Check if it's a full state name
  if (US_STATES[trimmed]) {
    return US_STATES[trimmed];
  }

  return null;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export function validateLeadRow(row: ParsedLeadRow, allowMissingPhone = false, allowMissingEmail = false): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Required field: Business Name
  if (!row.businessName || row.businessName.trim() === '') {
    errors.push({
      field: 'businessName',
      message: 'Business Name is required',
      severity: 'error'
    });
  }

  // Required field: Phone Number (unless allowMissingPhone is enabled)
  if (!row.phoneNumber || row.phoneNumber.trim() === '') {
    if (!allowMissingPhone) {
      errors.push({
        field: 'phoneNumber',
        message: 'Phone Number is required',
        severity: 'error'
      });
    } else {
      warnings.push({
        field: 'phoneNumber',
        message: 'Phone Number missing - placeholder will be used',
        severity: 'warning'
      });
    }
  } else {
    // Validate phone format (10 digits after cleaning) - always an error if invalid
    const cleaned = row.phoneNumber.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      errors.push({
        field: 'phoneNumber',
        message: `Phone Number must be 10 digits (found ${cleaned.length}): "${row.phoneNumber}"`,
        severity: 'error'
      });
    }
  }

  // Required field: Street Address - STRICT parsing
  if (!row.streetAddress || row.streetAddress.trim() === '') {
    errors.push({
      field: 'streetAddress',
      message: 'Street Address is required',
      severity: 'error'
    });
  } else {
    // Try to parse address - STRICT: both streetNumber and streetName required
    const parsed = parseAddress(row.streetAddress);
    if (!parsed || !parsed.streetNumber || !parsed.streetName) {
      errors.push({
        field: 'streetAddress',
        message: `Could not parse address into street number and street name: "${row.streetAddress}"`,
        severity: 'error'
      });
      row.parsedAddress = null;
    } else {
      row.parsedAddress = parsed;
    }
  }

  // Required field: City
  if (!row.city || row.city.trim() === '') {
    errors.push({
      field: 'city',
      message: 'City is required',
      severity: 'error'
    });
  }

  // Required field: State - parse both full name (Alabama) and abbreviation (AL)
  if (!row.state || row.state.trim() === '') {
    errors.push({
      field: 'state',
      message: 'State is required',
      severity: 'error'
    });
  } else {
    const parsedState = parseStateInput(row.state);
    if (!parsedState) {
      errors.push({
        field: 'state',
        message: `Invalid state: "${row.state}"`,
        severity: 'error'
      });
    }
  }

  // Required field: ZIP
  if (!row.zip || row.zip.trim() === '') {
    errors.push({
      field: 'zip',
      message: 'ZIP code is required',
      severity: 'error'
    });
  } else {
    // ZIP format validation (5 digits or 5+4 format)
    if (!/^\d{5}(-\d{4})?$/.test(row.zip.trim())) {
      warnings.push({
        field: 'zip',
        message: `ZIP code format may be invalid (expected 12345 or 12345-6789): "${row.zip}"`,
        severity: 'warning'
      });
    }
  }

  // Store errors and warnings on the row
  row.errors = errors;
  row.warnings = warnings;

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

export function validateAllRows(
  rows: ParsedLeadRow[],
  allowMissingPhone = false,
  allowMissingEmail = false
): { validRows: ParsedLeadRow[]; invalidRows: ParsedLeadRow[] } {
  const validRows: ParsedLeadRow[] = [];
  const invalidRows: ParsedLeadRow[] = [];

  for (const row of rows) {
    const result = validateLeadRow(row, allowMissingPhone, allowMissingEmail);
    if (result.valid) {
      validRows.push(row);
    } else {
      invalidRows.push(row);
    }
  }

  return { validRows, invalidRows };
}
